import React, { useMemo, useState, useEffect } from 'react'
import { Table, Input, Button } from 'antd'
import Highlighter from 'react-highlight-words'
import { SearchOutlined } from '@ant-design/icons'
import { useRecoilState } from 'recoil'
import { mutate } from 'swr'
import { useHistory, useLocation } from 'react-router-dom'
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table'
import {
	buildCacheKey,
	enumToArray,
	isObjectEmpty,
	capitalize,
	snakeCaseToTitle,
} from '../../../util'
import { exploreIncomesQueryState } from '../../../recoil'
import {
	EIncomeOriginTypeCorriente,
	EIncomeType,
	EIncomeOriginTypeCapital,
	EIncomeOriginNature,
} from '../../../typings'
import { useDebounce } from '../../../hooks'
import {
	IRecoilExploreIncomesPaginate,
	IRecoilExploreIncomesMatch,
} from '../../../typings/recoil'

interface Props {
	data?: { data: Entities.Income.Object[]; count: number }
}

const originTypeCorrienteFilters = enumToArray(EIncomeOriginTypeCorriente).map(
	(originType) => ({
		text: snakeCaseToTitle(originType),
		value: originType,
	})
)

const originTypeCapitalFilters = enumToArray(EIncomeOriginTypeCapital).map(
	(originType) => ({
		text: snakeCaseToTitle(originType),
		value: originType,
	})
)

const IncomesTable: React.FC<Props> = ({ data }) => {
	const history = useHistory()
	const location = useLocation()
	const [query, setQuery] = useRecoilState(exploreIncomesQueryState)
	const [textSearch, setTextSearch] = useState('')
	const { debouncedValue } = useDebounce(textSearch, 125)
	const handleItemClick = (
		e: React.MouseEvent<HTMLElement, MouseEvent>,
		income: Entities.Income.Object
	) => {
		e.preventDefault()
		history.push(location.pathname + `/${income._id}`, location.state) // preserve state (currently used only to display names instead of ID's in breadcrumbs)
		mutate(
			buildCacheKey('income', 'discrete', { documentID: income._id }),
			() => income
		)
	}

	const handlePageChange = (page: number) => {
		setQuery((current) => ({
			...current,
			paginate: {
				...current.paginate,
				page,
			},
		}))
	}

	const handleTableChange = (
		pagination: TablePaginationConfig,
		filters: Record<string, React.ReactText[] | null>,
		sorter: any
	) => {
		let match: IRecoilExploreIncomesMatch
		let sort: string
		let paginate: IRecoilExploreIncomesPaginate
		if (filters && !isObjectEmpty(filters)) {
			const originType: any = filters.originType?.length
				? filters.originType[0]
				: undefined

			const originNature: any = filters.originName?.length
				? filters.originName[0]
				: undefined

			const type: any = filters.type?.length ? filters.type[0] : undefined
			match = {
				...query.match,
				originType,
				type,
				originNature,
			}
		}

		if (sorter && !isObjectEmpty(sorter)) {
			const field: string | string[] = sorter.field
			const order: 'ascend' | 'descend' = sorter.order
			const resolvedField = typeof field === 'string' ? field : field.join('.')
			const resolvedFieldWithOrder =
				order === 'ascend' ? resolvedField : `-${resolvedField}`

			sort = resolvedFieldWithOrder
		}

		if (pagination && !isObjectEmpty(pagination)) {
			paginate = {
				page: pagination.current || 1,
				pageSize: pagination.pageSize || 10,
			}
		}

		setQuery((current) => ({
			...current,
			match,
			sort,
			paginate,
		}))
	}

	const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		e.preventDefault()
		setTextSearch(e.target.value)
	}

	const handleCleanTextSearch = (
		e: React.MouseEvent<HTMLElement, MouseEvent>
	) => {
		e.preventDefault()
		setTextSearch('')
	}

	const getOriginTypeFilters = (incomeType: EIncomeType | 'all') => {
		let filters
		switch (incomeType) {
			case EIncomeType.Capital:
				filters = originTypeCapitalFilters
				break

			case EIncomeType.Corriente:
				filters = originTypeCorrienteFilters
				break

			default:
				filters = originTypeCapitalFilters.concat(originTypeCorrienteFilters)

				break
		}

		return filters.sort(
			(a, b) => a.text.charAt(0).charCodeAt(0) - b.text.charAt(0).charCodeAt(0)
		)
	}

	const columns: ColumnsType<Entities.Income.Object> = useMemo(
		() => [
			{
				title: 'Ministerio',
				dataIndex: 'institution',
				render: (value: Entities.Institution.Object) => (
					<p>{value ? value.name : 'N/A'}</p>
				),
			},
			{
				title: 'Entidad',
				dataIndex: 'entity',
				render: (value: Entities.Entity.Object) => (
					<p>{value ? snakeCaseToTitle(value.name) : 'N/A'}</p>
				),
			},
			{
				title: 'Programa',
				dataIndex: 'program',
				render: (value: Entities.Program.Object) => (
					<p>{value ? snakeCaseToTitle(value.name) : 'N/A'}</p>
				),
			},
			{
				title: 'Tipo',
				dataIndex: 'type',
				render: (value: string) => <p>{capitalize(value)}</p>,
				filters: [
					{ text: 'Capital', value: 'capital' },
					{ text: 'Corriente', value: 'corriente' },
				],
				filterMultiple: false,
				filteredValue: [query.match.type || ''],
			},
			{
				title: 'Justificación',
				dataIndex: 'originType',
				render: (value: string) => (
					<p style={{ wordWrap: 'normal', wordBreak: 'keep-all' }}>
						{snakeCaseToTitle(value)}
					</p>
				),
				filters: getOriginTypeFilters(query.match?.type || 'all'),
				filterMultiple: false,
				filteredValue: [query.match.originType || ''],
				width: 150,
			},
			{
				title: 'Origen',
				dataIndex: 'originName',
				render: (value: string, { originNature }: Entities.Income.Object) => (
					<p
						style={{
							wordWrap: 'normal',
							wordBreak: 'keep-all',
						}}
					>
						{snakeCaseToTitle(value)}
						<span style={{ fontWeight: 'bold' }}>({originNature})</span>
					</p>
				),
				filters: [
					{ text: 'Privado', value: EIncomeOriginNature.Privado },
					{ text: 'Público', value: EIncomeOriginNature.Publico },
					{ text: 'Particular', value: EIncomeOriginNature.Particular },
				],
				filterMultiple: false,
				filteredValue: [query.match.originNature || ''],
				width: 175,
			},
			{
				title: 'Nota',
				dataIndex: 'note',
				render: (value: string) => (
					<p
						style={{
							fontStyle: 'italic',
							wordWrap: 'break-word',
							wordBreak: 'break-word',
						}}
					>
						<Highlighter
							highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
							searchWords={[query.textSearch || '']}
							autoEscape
							textToHighlight={value}
						/>
					</p>
				),
				filterDropdown: () => (
					<div style={{ padding: 8 }}>
						<Input
							placeholder={'Buscar entre notas'}
							value={textSearch}
							style={{ width: 180, marginBottom: 8, display: 'block' }}
							onChange={handleTextChange}
						/>
						<Button
							size='small'
							style={{ width: '11.25rem' }}
							onClick={handleCleanTextSearch}
						>
							Limpiar
						</Button>
					</div>
				),
				filterIcon: <SearchOutlined />,
			},
			{
				title: 'Monto',
				dataIndex: 'amount',
				render: (value: string) => (
					<p style={{ color: '#1890ff', fontWeight: 'bold' }}>${value}</p>
				),
				sorter: () => {
					// void sorter to tell antd table to render the arrows (but we'll control the logic externally in handleTableChange)
					return 0
				},
			},
			{
				title: 'Fecha',
				dataIndex: ['metadata', 'createdAt'],
				render: (value: string) => new Date(value).toLocaleDateString(),
				sorter: () => {
					// void sorter to tell antd table to render the arrows (but we'll control the logic externally in handleTableChange)
					return 0
				},
			},
		],
		[query.match, query.textSearch, textSearch]
	)

	useEffect(() => {
		setQuery((current) => ({
			...current,
			textSearch: debouncedValue.trim(),
		}))
	}, [debouncedValue, setQuery])

	return (
		<Table
			dataSource={data?.data}
			rowKey='_id'
			loading={!data}
			onChange={handleTableChange}
			columns={columns}
			style={{ borderRadius: '5px' }}
			onRow={(income) => ({
				onClick: (e) => {
					handleItemClick(e, income)
				},
				style: {
					cursor: 'pointer',
				},
			})}
			pagination={{
				position: ['topLeft', 'bottomLeft'],
				current: query.paginate.page,
				pageSize: query.paginate.pageSize,
				total: data?.count,
				onChange: handlePageChange,
				showSizeChanger: true,
				pageSizeOptions: ['5', '10', '20', '50'],
				showTotal: (total, [from, to]) => `${from} - ${to} de ${total}`,
			}}
		/>
	)
}

export default IncomesTable
